/**
 * KAN-464: real-data time estimates for course-timeline cards.
 *
 * The timeline previously showed a hardcoded shape-based estimate ("15-20 min"
 * for every Video+Quiz module) regardless of the actual video length. This
 * util prefers real content data:
 *
 *  - Video time: the item's actual videoDuration (seconds), rounded UP to
 *    whole minutes (never below 1 minute).
 *  - Quiz time: SECONDS_PER_QUIZ_QUESTION per question, rounded UP to whole
 *    minutes. 60s/question is a defensible midpoint for multiple-choice /
 *    short-answer items and keeps a derived quiz-only estimate ("~N min")
 *    inside the legacy "5-10 min" band for typical 5-10 question quizzes.
 *
 * FALLBACK (required): ~25% of module videos have NULL duration in the live
 * DB (many stem from the KAN-428 stuck-finalizing webhook backlog), so when
 * no real video duration exists the legacy shape-based range strings are
 * kept verbatim. Derived estimates carry a "~" prefix so real-data numbers
 * are distinguishable from the legacy guesses.
 */

/** Estimated seconds a learner spends per quiz question. */
export const SECONDS_PER_QUIZ_QUESTION = 60;

/**
 * Quiz allowance (minutes) for a video+quiz module whose video duration is
 * real but whose question payload is unavailable. Lower bound of the legacy
 * "5-10 min" quiz-only band.
 */
export const DEFAULT_QUIZ_MINUTES = 5;

/** Structural subset of CourseNavigationItem the estimate depends on. */
export interface DurationEstimateItem {
  type: string;
  hasVideo?: boolean;
  hasQuiz?: boolean;
  /** Real video runtime in seconds (NULL/0/undefined = unknown). */
  videoDuration?: number;
  quiz?: readonly unknown[];
}

/** Legacy shape-based estimates, kept as the no-real-data fallback. */
const LEGACY = {
  unitQuiz: '10-15 min',
  videoAndQuiz: '15-20 min',
  videoOnly: '8-12 min',
  quizOnly: '5-10 min',
  textOnly: '3-5 min'
} as const;

/** Whole minutes (>= 1) from a real seconds value; null when unknown. */
function videoMinutes(item: DurationEstimateItem): number | null {
  if (!item.videoDuration || item.videoDuration <= 0) return null;
  return Math.max(1, Math.ceil(item.videoDuration / 60));
}

/** Whole minutes (>= 1) from the question count; null when count unknown. */
function quizMinutes(item: DurationEstimateItem): number | null {
  const count = item.quiz?.length ?? 0;
  if (count <= 0) return null;
  return Math.max(1, Math.ceil((count * SECONDS_PER_QUIZ_QUESTION) / 60));
}

/** "~N min" under an hour; "~Xh" / "~Xh Ym" above. */
function formatEstimate(totalMinutes: number): string {
  if (totalMinutes < 60) return `~${totalMinutes} min`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `~${hours}h` : `~${hours}h ${minutes}m`;
}

/**
 * Time-estimate label for a course-timeline card. Prefers real data (video
 * duration, quiz question count); falls back to the legacy shape-based
 * range strings when no real video duration is available.
 */
export function estimateDurationLabel(item: DurationEstimateItem): string {
  if (item.type === 'course_summary') return 'View Results';
  if (item.type === 'unit_intro') return '2 min read';

  if (item.type === 'unit_quiz') {
    const quizMin = quizMinutes(item);
    return quizMin !== null ? formatEstimate(quizMin) : LEGACY.unitQuiz;
  }

  const videoMin = videoMinutes(item);
  const quizMin = quizMinutes(item);

  if (item.hasVideo && item.hasQuiz) {
    if (videoMin === null) return LEGACY.videoAndQuiz;
    return formatEstimate(videoMin + (quizMin ?? DEFAULT_QUIZ_MINUTES));
  }
  if (item.hasVideo) {
    return videoMin !== null ? formatEstimate(videoMin) : LEGACY.videoOnly;
  }
  if (item.hasQuiz) {
    return quizMin !== null ? formatEstimate(quizMin) : LEGACY.quizOnly;
  }
  return LEGACY.textOnly;
}
